// Global Instructions Rule Applied!
//
// HTTP layer for stage-aging alerts (Phase 6): the navbar bell read, read
// state, and the per-pipeline recipient editor (Configuration → Pipelines).
// Talks to the Python backend only; the bell payload is Zod-validated before
// use (invalid payloads are an error, never coerced).
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  StageAlertsResponseModel,
  validateStageAlertsResponse
} from '../../../state_manager/data_models/stage_alerts_model.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const authHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

const errorMessage = async (response: Response, fallback: string): Promise<string> => {
  if (response.status === 401) return 'Authentication failed - please log in again'
  const body = await readJsonBody<{ message?: string }>(response)
  return body?.message || `${fallback}: ${response.status} ${response.statusText}`
}

export const fetchStageAlerts = async (
  accessToken: string | null
): Promise<{ data: StageAlertsResponseModel | null; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.STAGE_ALERTS.GET), {
      method: API_ROUTES.STAGE_ALERTS.GET.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to fetch stage alerts'))
    const json = (await response.json()) as { data?: unknown }
    return { data: validateStageAlertsResponse(json.data) }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'Unexpected error' }
  }
}

/** Mark specific alerts read, or all visible alerts when alertIds is omitted. */
export const markStageAlertsRead = async (
  alertIds: string[] | null,
  accessToken: string | null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.STAGE_ALERTS.MARK_READ), {
      method: API_ROUTES.STAGE_ALERTS.MARK_READ.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: alertIds ? { alert_ids: alertIds } : {} })
    })
    if (!response.ok) return { success: false, error: await errorMessage(response, 'Failed to mark alerts read') }
    return { success: true }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unexpected error' }
  }
}

export interface DigestSchedule {
  frequency_unit: 'daily' | 'weekly' | 'monthly'
  interval_count: number
  weekday: number | null
  day_of_month: number | null
  enabled: boolean
  next_send_dates: string[]
}

const scheduleFromPayload = (data: Record<string, unknown> | undefined): DigestSchedule => ({
  frequency_unit:
    (data?.['stage_alert_digest_schedules__frequency_unit'] as DigestSchedule['frequency_unit']) || 'daily',
  interval_count: (data?.['stage_alert_digest_schedules__interval_count'] as number) ?? 1,
  weekday: (data?.['stage_alert_digest_schedules__weekday'] as number | null) ?? null,
  day_of_month: (data?.['stage_alert_digest_schedules__day_of_month'] as number | null) ?? null,
  enabled: (data?.['stage_alert_digest_schedules__enabled'] as boolean) ?? true,
  next_send_dates: (data?.['next_send_dates'] as string[]) || []
})

export const fetchDigestSchedule = async (
  accessToken: string | null
): Promise<{ data: DigestSchedule | null; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.STAGE_ALERTS.DIGEST_SCHEDULE_GET), {
      method: API_ROUTES.STAGE_ALERTS.DIGEST_SCHEDULE_GET.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to fetch digest schedule'))
    const json = (await response.json()) as { data?: Record<string, unknown> }
    return { data: scheduleFromPayload(json.data) }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'Unexpected error' }
  }
}

export const saveDigestSchedule = async (
  schedule: Pick<DigestSchedule, 'frequency_unit' | 'interval_count' | 'weekday' | 'day_of_month' | 'enabled'>,
  accessToken: string | null
): Promise<{ data: DigestSchedule | null; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.STAGE_ALERTS.DIGEST_SCHEDULE_SET), {
      method: API_ROUTES.STAGE_ALERTS.DIGEST_SCHEDULE_SET.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: schedule })
    })
    if (!response.ok) return { data: null, error: await errorMessage(response, 'Failed to save digest schedule') }
    const json = (await response.json()) as { data?: Record<string, unknown> }
    return { data: scheduleFromPayload(json.data) }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'Unexpected error' }
  }
}

/** Full-replace a pipeline's stage-alert recipient email list. */
export const setStageAlertRecipients = async (
  pipelineId: string,
  emails: string[],
  accessToken: string | null
): Promise<{ success: boolean; recipients?: { stage_alert_recipients__id: string; stage_alert_recipients__email: string }[]; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.STAGE_ALERTS.RECIPIENTS_SET, { pipelineId }),
      {
        method: API_ROUTES.STAGE_ALERTS.RECIPIENTS_SET.method,
        headers: authHeaders(accessToken),
        body: JSON.stringify({ data: { emails } })
      }
    )
    if (!response.ok) return { success: false, error: await errorMessage(response, 'Failed to save recipients') }
    const json = (await response.json()) as {
      data?: { recipients?: { stage_alert_recipients__id: string; stage_alert_recipients__email: string }[] }
    }
    return { success: true, recipients: json.data?.recipients || [] }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unexpected error' }
  }
}
